// order-form 주문 제출 API 에러를 UI 액션으로 분류하는 순수 로직. bunja/action.tsx(submitOrder catch)가 소비한다.
// 메시지 조립(i18n/서버 메시지)·토스트·다이얼로그·인라인 에러 세팅 같은 부수효과는 호출 측이 담당한다.
import { checkAxiosError, type ErrorResponseV2Error } from '@flipster/utils/axios';
import { getErrorCode } from '@flipster/utils/error';

import type { OrderState } from '../../form';

type InlineField = 'quantity' | 'amount' | 'margin';

/** NotEnoughBalance 서버 details + 주문 kind → 인라인 에러를 붙일 입력 필드. */
function resolveInlineField(details: string | undefined, orderKind: OrderState['kind']): InlineField {
  if (details === 'Qty') return 'quantity';
  if (details === 'Amount') return orderKind === 'margin' ? 'margin' : 'amount';
  return orderKind;
}

export type SubmitErrorAction =
  | { type: 'autoLeverageRestricted' } // 자동 레버리지 조정 정책 → 다이얼로그 안내 (onOrderFailed 호출 안 함)
  | { type: 'slippageExceeded' } // ERD-233: 허용 슬리피지 내 0-fill → localized 토스트
  | { type: 'notEnoughBalance'; field: InlineField } // 잔액 부족 → 해당 입력 필드 인라인 에러 + 토스트
  | { type: 'unknown' }; // 그 외 → 서버 메시지 토스트

/**
 * 주문 제출 API 에러를 UI 액션으로 분류한다(순수).
 * errorCode 판정 + NotEnoughBalance의 인라인 필드 결정만 담당하고, 메시지·부수효과는 호출 측이 처리한다.
 */
export function classifySubmitError(error: unknown, orderKind: OrderState['kind']): SubmitErrorAction {
  switch (getErrorCode(error)) {
    case 'AccountMaxInitLeverageRestricted':
      return { type: 'autoLeverageRestricted' };
    case 'SlippageExceeded':
      return { type: 'slippageExceeded' };
    case 'NotEnoughBalance': {
      let details: string | undefined;
      if (checkAxiosError(error)) {
        const err = error.response?.data?.error;
        if (err && typeof err === 'object' && 'details' in err) {
          details = (err as ErrorResponseV2Error).details;
        }
      }
      return { type: 'notEnoughBalance', field: resolveInlineField(details, orderKind) };
    }
    default:
      return { type: 'unknown' };
  }
}
